<script setup lang="ts">
import { computed } from "vue";
import { useDialogLabel } from "../../composables/Dialog/useDialogLabel";
import { tryUseDialogLayer } from "../../composables/Dialog/useDialogLayer";
import VHelpTooltip from "../HelpTooltip/VHelpTooltip.vue";
import { closeReserveClass } from "./dialogSizes";

defineOptions({
  name: "VDialogHeader",
});

/**
 * Header of a dialog: optional leading media (app logo or icon tile), the title with optional pills beside it, and
 * a grey subtitle line under it. The dialog host draws the 44px close button at a 12px inset;
 * `reserveClose` keeps the title clear of it: from the 20px dialog gutter, or from the dialog edge in a paddingless
 * dialog where the header brings its own gutter.
 *
 * With `bar` it is the full-width header bar of a paddingless dialog: the hairline under it, 64px high on the 16px
 * gutter of a settings dialog or 72px on the 28px gutter of the App Store editor, and the body's own gutter on both
 * sides, so header and body share their edges. The media sits on the left gutter with the title 12px after it, also
 * above a vertical tab rail: the title is never pushed out to the form column. The host close moves to the right gutter, in line with the form's right edge, centred in the bar.
 */
const {
  title,
  subtitle = "",
  headingLevel = 2,
  size = "md",
  reserveClose = true,
  subtitleGap = 2,
  help = "",
  bar = false,
  gutter = 16,
} = defineProps<{
  title: string
  /** Grey line under the title, for data only (an organisation, a path, a date). Explanations go in `help`. */
  subtitle?: string
  /** Help text behind a question mark after the title; never a line under it. */
  help?: string
  headingLevel?: 1 | 2 | 3
  /** `md` is the 20px title of a dialog, `sm` the 19px title of a compact dialog, `lg` the 22px title of a page-sized wizard step. */
  size?: "sm" | "md" | "lg"
  reserveClose?: boolean
  /** Space in px between title and subtitle: 2 on most dialogs, 4 on a page-sized step, 6 under a log detail title. */
  subtitleGap?: 2 | 4 | 6
  /** The header bar of a paddingless dialog, on the body's gutter with the close in line with the form's right edge. */
  bar?: boolean
  /** With `bar`: the body's side padding in px (16 for a settings dialog, 28 for the App Store editor). */
  gutter?: 16 | 28
}>();

defineSlots<{
  /** 40 to 44px logo or icon tile before the title. */
  media?: () => unknown
  /** Pills or chips beside the title (e.g. "Gepubliceerd"). */
  meta?: () => unknown
  /** Rich subtitle instead of `subtitle` (e.g. a method chip and a path). */
  subtitle?: () => unknown
  /** With `bar`: controls at the end of the bar, before the close (e.g. a language switch). */
  actions?: () => unknown
}>();

const layer = tryUseDialogLayer();
const labelId = useDialogLabel("title");

const headingTag = computed(() => `h${headingLevel}`);
// Clear of the host close: the close, its 12px inset and an 8px gap from the dialog edge (see closeReserveClass).
const reserveClass = computed(() => {
  if (!reserveClose) {
    return undefined;
  }
  return closeReserveClass(Boolean(layer?.paddingless));
});
const SUBTITLE_GAP_CLASS = { 2: "gap-0.5", 4: "gap-1", 6: "gap-1.5" } as const;
const gapClass = computed(() => SUBTITLE_GAP_CLASS[subtitleGap]);
const TITLE_CLASSES = {
  sm: "text-[19px] leading-6",
  md: "text-xl leading-6",
  lg: "text-[22px] leading-7",
} as const;

const titleClass = computed(() => TITLE_CLASSES[size]);

/**
 * The bar's side padding is the body's gutter. On the right it also keeps the title clear of the host close, which
 * the dialog moves to that gutter: gutter + the 44px close + an 8px gap. Media and title sit 12px apart on the
 * settings boards and 14px apart on the App Store editor board.
 */
const BAR_GUTTER = {
  16: { left: "pl-4", right: "pr-4", gap: "gap-3", height: "min-h-16", reserve: "pr-17" },
  28: { left: "pl-7", right: "pr-7", gap: "gap-3.5", height: "min-h-18", reserve: "pr-20" },
} as const;

const headerClass = computed(() => {
  if (!bar) {
    return ["items-start gap-3.5", reserveClass.value];
  }
  const spacing = BAR_GUTTER[gutter];
  return [
    "items-center border-b border-gray-100 py-2 dark:border-dark-600",
    spacing.height,
    spacing.gap,
    spacing.left,
    reserveClose ? spacing.reserve : spacing.right,
  ];
});
</script>

<template>
  <header
    class="flex min-w-0"
    :class="headerClass"
    :data-dialog-header-bar="bar ? gutter : undefined"
  >
    <slot name="media" />
    <div class="flex min-w-0 flex-1 flex-col" :class="gapClass" data-testid="dialog-header-text">
      <div class="flex min-w-0 flex-wrap items-center gap-2.5">
        <component :is="headingTag" :id="labelId" class="m-0 min-w-0 font-bold text-(--text)" :class="titleClass">
          {{ title }}
        </component>
        <VHelpTooltip v-if="help" :content="help" :subject="title" />
        <slot name="meta" />
      </div>
      <div
        v-if="subtitle || $slots.subtitle"
        class="flex min-w-0 flex-wrap items-center gap-2 text-[13px] text-gray-700 dark:text-gray-400"
        :class="bar ? 'leading-4' : undefined"
      >
        <slot name="subtitle">
          {{ subtitle }}
        </slot>
      </div>
    </div>
    <div v-if="bar && $slots.actions" class="flex shrink-0 items-center gap-2">
      <slot name="actions" />
    </div>
  </header>
</template>
